import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SignOut } from '@platform-frontend/account';
import { CreditCardService, DirectDebitService } from '@platform-frontend/payment-util';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  ClearNotifications,
  HttpChangedResponse,
  InvalidStateError,
  PlannedError,
  RefreshWindow,
  ShowErrorNotification,
  convertToHttpParams,
  handleErrors,
  tapNextOrError
} from '@platform-frontend/util';
import { MonoTypeOperatorFunction, Observable, defer, of, throwError } from 'rxjs';
import { filter, map, switchMap, tap, timeout } from 'rxjs/operators';
import {
  MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_DEFAULT,
  MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_REDIRECT_ID_NOT_KNOWN,
  MSG_CREATE_UPDATE_CREDIT_CARD_AUTHORIZATION_REFUSED,
  MSG_CREATE_UPDATE_CREDIT_CARD_DEFAULT,
  MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTED_DATA_EXPIRED,
  MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTION_FAILED,
  MSG_CREATE_UPDATE_CREDIT_CARD_INVALID_ENCRYPTED_DATA,
  MSG_CREATE_UPDATE_CREDIT_CARD_NOT_FOUND,
  MSG_CREATE_UPDATE_CREDIT_CARD_OUTDATED_PAYMENT_INFORMATION_VERSION,
  MSG_CREATE_UPDATE_CREDIT_CARD_PAYMENT_METHOD_NOT_FOUND,
  MSG_CREATE_UPDATE_CREDIT_CARD_REDIRECT_ID_NOT_KNOWN,
  MSG_CREATE_UPDATE_CREDIT_CARD_REJECTED_PAYMENT,
  MSG_CREATE_UPDATE_CREDIT_CARD_TRANSACTION_WITH_SOLUTION_ID_IN_USE,
  MSG_CREATE_UPDATE_DIRECT_DEBIT_DEFAULT,
  MSG_CREATE_UPDATE_DIRECT_DEBIT_NOT_FOUND,
  MSG_CREATE_UPDATE_DIRECT_DEBIT_OUTDATED_PAYMENT_INFORMATION_VERSION,
  MSG_CREATE_UPDATE_DIRECT_DEBIT_TRANSACTION_WITH_SOLUTION_ID_IN_USE,
  MSG_CREATE_UPDATE_PAYPAL_DEFAULT,
  MSG_DELETE_PAYMENT_METHOD_DEFAULT,
  MSG_DELETE_PAYMENT_METHOD_NOT_FOUND,
  MSG_DELETE_PAYMENT_METHOD_OUTDATED_PAYMENT_INFORMATION_VERSION,
  MSG_DELETE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE
} from '../notification-messages';
import { MARKETPLACE_ERROR_HANDLERS } from './../marketplace-error-handlers';
import {
  AddWalletPaymentMethod,
  CheckPaymentMethodVerification,
  ClearPaymentMethodVerification,
  CreatePaymentMethod,
  CreatePaymentMethodWithVerification,
  DeletePaymentMethod,
  FetchPaymentMethods,
  PaymentMethodCreated,
  PaymentMethodDeleted,
  PaymentMethodUpdated,
  ResetPaymentMethods,
  SetPaymentMethods,
  UpdatePaymentMethod
} from './marketplace-payment-information.actions';
import { CreatePaymentModel } from './model/create-payment-model';
import { MarketplacePaymentInformationStateModel } from './model/marketplace-payment-information-state-model';
import { PaymentMethod, isReusablePaymentMethod } from './model/payment-method';
import { PaymentMethodBackendIdentifier } from './model/payment-method-backend-identifier';
import { PaymentMethodIdentifier, isReusablePaymentMethodIdentifier } from './model/payment-method-identifier';
import { PaymentMethodInfo } from './model/payment-method-info';
import { PaymentMethodRedirection } from './model/payment-method-redirection';
import { PaymentMethodVerification, SuccessfulPaymentMethodVerification } from './model/payment-method-verification';
import { PaymentMethodInfoWithIndex } from './model/payment-method-with-index';
import { EditablePaymentMethod } from './model/reusable-payment-method/editable-payment-method';
import { ReusablePaymentMethod } from './model/reusable-payment-method/reusable-payment-method';
import { PaymentInformationService } from './payment-information.service';
import { ScaService } from './sca.service';

const DEFAULT_STATE: MarketplacePaymentInformationStateModel = {
  paymentMethods: [],
  redirection: null
};

@State<MarketplacePaymentInformationStateModel>({
  name: 'marketplacePaymentInformation',
  defaults: DEFAULT_STATE
})
@Injectable()
export class MarketplacePaymentInformationState {
  constructor(
    private creditCardService: CreditCardService,
    private directDebitService: DirectDebitService,
    private http: HttpClient,
    private paymentInformationService: PaymentInformationService,
    private scaService: ScaService
  ) {}

  private static readonly POLLING_TAG = Symbol('PaymentInformationState: PollingTag');

  @Selector()
  static paymentMethods(state: MarketplacePaymentInformationStateModel): PaymentMethodInfo[] {
    return state.paymentMethods;
  }

  @Selector()
  static redirection(state: MarketplacePaymentInformationStateModel): PaymentMethodRedirection | null {
    return state.redirection;
  }

  @Action(SignOut)
  @Action(ResetPaymentMethods)
  reset(ctx: StateContext<MarketplacePaymentInformationStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(SetPaymentMethods)
  setBillingAddresses(ctx: StateContext<MarketplacePaymentInformationStateModel>, { paymentMethods }: SetPaymentMethods): void {
    ctx.patchState({ paymentMethods });
  }

  @Action(FetchPaymentMethods)
  refreshPaymentMethods(ctx: StateContext<MarketplacePaymentInformationStateModel>): Observable<unknown> {
    return this.paymentInformationService.fetchPaymentMethods().pipe(switchMap(paymentMethods => ctx.dispatch(new SetPaymentMethods(paymentMethods))));
  }

  @Action(AddWalletPaymentMethod)
  addWalletPaymentMethod(ctx: StateContext<MarketplacePaymentInformationStateModel>, { walletType }: AddWalletPaymentMethod): Observable<unknown> {
    return defer(() => of(ctx.getState().paymentMethods.findIndex(method => method.type === walletType) > -1)).pipe(
      switchMap(walletAlreadyAdded =>
        walletAlreadyAdded
          ? throwError(() => new InvalidStateError('Wallet is already present'))
          : of({ type: walletType, serversideVersion: null, contractIdsForConnectedSubscriptions: [] })
      ),
      tap(walletPaymentMethod => this.createPaymentMethodLocally(ctx, walletPaymentMethod)),
      switchMap(createdPaymentMethod => ctx.dispatch([new ClearNotifications(), new PaymentMethodCreated(createdPaymentMethod)])),
      handleErrors(MARKETPLACE_ERROR_HANDLERS)
    );
  }

  @Action(CreatePaymentMethod)
  createPaymentMethod(ctx: StateContext<MarketplacePaymentInformationStateModel>, { paymentMethod, skipSca }: CreatePaymentMethod): Observable<unknown> {
    return defer(() => of(this.normalizeEditablePaymentMethod(ctx, paymentMethod, skipSca))).pipe(
      switchMap(normalizedPaymentMethod =>
        this.http.post<CreatePaymentModel>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo', normalizedPaymentMethod).pipe(
          switchMap(createPaymentModel => this.performScaIfNecessary(createPaymentModel)),
          map(({ id, serversideVersion }) => ({ ...normalizedPaymentMethod, id, serversideVersion }))
        )
      ),
      tap(createdPaymentMethod => this.createPaymentMethodLocally(ctx, createdPaymentMethod)),
      switchMap(createdPaymentMethod => ctx.dispatch([new ClearNotifications(), new PaymentMethodCreated(createdPaymentMethod)])),
      this.handleErrorsForEditableMethod(paymentMethod.type)
    );
  }

  private performScaIfNecessary(createPaymentModel: CreatePaymentModel): Observable<ReusablePaymentMethod> {
    if (createPaymentModel.redirect === null) {
      return of(createPaymentModel.paymentMethod);
    }

    if (createPaymentModel.paymentMethod.type !== 'CREDIT_CARD') {
      throw new InvalidStateError(`SCA requested for payment method of type ${createPaymentModel.paymentMethod.type}`);
    }

    return this.scaService
      .performSca(createPaymentModel.redirect, createPaymentModel.paymentMethod.cardType, 'creation')
      .pipe(map(() => createPaymentModel.paymentMethod));
  }

  @Action(UpdatePaymentMethod)
  updatePaymentMethod(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    { paymentMethod, overrideSubscription }: UpdatePaymentMethod
  ): Observable<unknown> {
    return defer(() => of(this.normalizeEditablePaymentMethod(ctx, paymentMethod, false))).pipe(
      switchMap(normalizedPaymentMethod =>
        this.http
          .put<HttpChangedResponse<ReusablePaymentMethod>>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo', {
            ...normalizedPaymentMethod,
            contractOrProduct: overrideSubscription
          })
          .pipe(
            map(({ changed, response }) => ({
              changed,
              normalizedPaymentMethod,
              updatedPaymentMethod: { ...normalizedPaymentMethod, id: response.id, serversideVersion: response.serversideVersion } as ReusablePaymentMethod
            }))
          )
      ),
      switchMap(({ changed, updatedPaymentMethod, normalizedPaymentMethod }) =>
        this.updatePaymentMethodLocally(ctx, normalizedPaymentMethod, updatedPaymentMethod).pipe(
          map(() => ({
            changed,
            updatedPaymentMethod,
            normalizedPaymentMethod
          }))
        )
      ),
      this.handleErrorsForEditableMethod(paymentMethod.type),
      switchMap(({ changed, updatedPaymentMethod, normalizedPaymentMethod }) =>
        ctx.dispatch([new ClearNotifications(), new PaymentMethodUpdated(normalizedPaymentMethod, updatedPaymentMethod, changed)])
      )
    );
  }

  private updatePaymentMethodLocally(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    original: ReusablePaymentMethod,
    update: ReusablePaymentMethod
  ): Observable<unknown> {
    const methods = ctx.getState().paymentMethods;
    const { index } = this.getPaymentMethodWithIndex(ctx, { id: original.id });

    return ctx.dispatch(new SetPaymentMethods([update, ...methods.slice(0, index), ...methods.slice(index + 1)]));
  }

  private normalizeEditablePaymentMethod(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    paymentMethod: EditablePaymentMethod,
    skipSca: boolean
  ): ReusablePaymentMethod {
    const savedMethods: ReusablePaymentMethod[] = ctx
      .getState()
      .paymentMethods.filter((method): method is ReusablePaymentMethod => isReusablePaymentMethod(method));
    const savedMethod: ReusablePaymentMethod | undefined = savedMethods.find(method => method.id === paymentMethod.id);
    switch (paymentMethod.type) {
      case 'CREDIT_CARD': {
        const details = this.creditCardService.getDetails(paymentMethod);
        return {
          type: 'CREDIT_CARD',
          refusedPayment: false,
          id: paymentMethod.id,
          holderName: details.holderName,
          expiryMonth: details.expiryMonth,
          expiryYear: details.expiryYear,
          cardType: details.cardType,
          maskedNumber: details.maskedNumber,
          encryptedString: details.encryptedString,
          skipSca: skipSca,
          contractIdsForConnectedSubscriptions: savedMethod?.contractIdsForConnectedSubscriptions ?? [],
          serversideVersion: savedMethod?.serversideVersion ?? null
        };
      }
      case 'DIRECT_DEBIT': {
        return {
          type: 'DIRECT_DEBIT',
          refusedPayment: false,
          id: paymentMethod.id,
          accountOwner: paymentMethod.accountOwner.trim(),
          iban: this.directDebitService.simplifyIban(paymentMethod.iban),
          bank: paymentMethod.bank?.trim() ?? null,
          bic: paymentMethod.bic?.trim() ?? null,
          contractIdsForConnectedSubscriptions: savedMethod?.contractIdsForConnectedSubscriptions ?? [],
          serversideVersion: savedMethod?.serversideVersion ?? null
        };
      }
      default: {
        throw new InvalidStateError('Invalid payment method: ' + (paymentMethod as PaymentMethod).type);
      }
    }
  }

  private handleErrorsForEditableMethod<T>(type: EditablePaymentMethod['type']): MonoTypeOperatorFunction<T> {
    switch (type) {
      case 'CREDIT_CARD':
        return this.handleCreditCardErrors();
      case 'DIRECT_DEBIT':
        return this.handleDirectDebitErrors();
    }
  }

  private handleCreditCardErrors<T>(): MonoTypeOperatorFunction<T> {
    return obs$ =>
      obs$.pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS, {
          DEFAULT: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_DEFAULT),
          ENCRYPTION_FAILED: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTION_FAILED),
          NOT_FOUND: new RefreshWindow(MSG_CREATE_UPDATE_CREDIT_CARD_NOT_FOUND),
          TRANSACTION_WITH_SOLUTION_ID_IN_USE: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_TRANSACTION_WITH_SOLUTION_ID_IN_USE),
          OUTDATED_PAYMENT_INFORMATION_VERSION: new RefreshWindow(MSG_CREATE_UPDATE_CREDIT_CARD_OUTDATED_PAYMENT_INFORMATION_VERSION),
          ENCRYPTED_DATA_EXPIRED: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTED_DATA_EXPIRED),
          INVALID_ENCRYPTED_DATA: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_INVALID_ENCRYPTED_DATA),
          PAYMENT_METHOD_NOT_FOUND: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_PAYMENT_METHOD_NOT_FOUND),
          REJECTED_PAYMENT: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_REJECTED_PAYMENT),

          // validation errors
          CVC_DECLINED: new ClearNotifications(),
          CARD_EXPIRED: new ClearNotifications(),
          EXPIRY_DATE_INVALID: new ClearNotifications(),
          INVALID_CARD_NUMBER: new ClearNotifications(),

          // sca errors
          REDIRECT_ID_NOT_KNOWN: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_REDIRECT_ID_NOT_KNOWN),
          AUTHORIZATION_REFUSED: new ShowErrorNotification(MSG_CREATE_UPDATE_CREDIT_CARD_AUTHORIZATION_REFUSED),
          CANCELED_BY_USER: () => undefined
        })
      );
  }

  private handleDirectDebitErrors<T>(): MonoTypeOperatorFunction<T> {
    return obs$ =>
      obs$.pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS, {
          DEFAULT: new ShowErrorNotification(MSG_CREATE_UPDATE_DIRECT_DEBIT_DEFAULT),
          NOT_FOUND: new RefreshWindow(MSG_CREATE_UPDATE_DIRECT_DEBIT_NOT_FOUND),
          TRANSACTION_WITH_SOLUTION_ID_IN_USE: new ShowErrorNotification(MSG_CREATE_UPDATE_DIRECT_DEBIT_TRANSACTION_WITH_SOLUTION_ID_IN_USE),
          OUTDATED_PAYMENT_INFORMATION_VERSION: new RefreshWindow(MSG_CREATE_UPDATE_DIRECT_DEBIT_OUTDATED_PAYMENT_INFORMATION_VERSION)
        })
      );
  }

  @Action(CreatePaymentMethodWithVerification)
  createPaymentMethodWithVerification(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    { type }: CreatePaymentMethodWithVerification
  ): Observable<unknown> {
    ctx.patchState({ redirection: null });
    return this.http.post<CreatePaymentModel>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo', { type }).pipe(
      tap(createPaymentModel => ctx.patchState({ redirection: createPaymentModel.redirect })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_CREATE_UPDATE_PAYPAL_DEFAULT)
      })
    );
  }

  @Action(CheckPaymentMethodVerification)
  checkPaymentMethodVerification(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    { timeoutInMilliseconds }: CheckPaymentMethodVerification
  ): Observable<unknown> {
    const redirectId = ctx.getState().redirection?.redirectId;
    if (redirectId === undefined) {
      throw new InvalidStateError('Cannot poll for payment method verification: No redirect id is set.');
    }

    const params = convertToHttpParams({ redirectId });
    return this.http.get<PaymentMethodVerification>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo/verified', { params }).pipe(
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(MarketplacePaymentInformationState.POLLING_TAG))),
      filter((response): response is SuccessfulPaymentMethodVerification => response.verified),
      switchMap(({ paymentInformation }) => this.createPaymentMethodLocally(ctx, paymentInformation).pipe(map(() => ({ paymentInformation })))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_DEFAULT, { tags: [MarketplacePaymentInformationState.POLLING_TAG] }),
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_DEFAULT, {
          tags: [MarketplacePaymentInformationState.POLLING_TAG]
        }),
        REDIRECT_ID_NOT_KNOWN: new ShowErrorNotification(MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_REDIRECT_ID_NOT_KNOWN),
        CANCELED_BY_USER: () => undefined
      }),
      switchMap(({ paymentInformation }) => ctx.dispatch(new PaymentMethodCreated(paymentInformation)))
    );
  }

  @Action(ClearPaymentMethodVerification)
  clearPaymentMethodVerification(ctx: StateContext<MarketplacePaymentInformationStateModel>): Observable<unknown> {
    ctx.patchState({ redirection: null });
    return ctx.dispatch(new ClearNotifications(MarketplacePaymentInformationState.POLLING_TAG));
  }

  private createPaymentMethodLocally(ctx: StateContext<MarketplacePaymentInformationStateModel>, paymentMethod: PaymentMethodInfo): Observable<unknown> {
    const methods = ctx.getState().paymentMethods;
    ctx.patchState({ redirection: null });
    return ctx.dispatch(new SetPaymentMethods([paymentMethod, ...methods]));
  }

  @Action(DeletePaymentMethod)
  deletePaymentMethod(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    { paymentMethodIdentifier, deleteOnlyLocally }: DeletePaymentMethod
  ): Observable<unknown> {
    const paymentMethodWithIndex = this.getPaymentMethodWithIndex(ctx, paymentMethodIdentifier);
    const paymentMethod = paymentMethodWithIndex.paymentMethodInfo;
    const params = convertToHttpParams<PaymentMethodBackendIdentifier>(
      isReusablePaymentMethod(paymentMethod)
        ? { id: paymentMethod.id, serversideVersion: paymentMethod.serversideVersion }
        : { type: paymentMethod.type, serversideVersion: paymentMethod.serversideVersion }
    );

    const deleteRequest$: Observable<void | null> = deleteOnlyLocally
      ? of(null)
      : this.http.delete<void>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo', { params });
    return deleteRequest$.pipe(
      switchMap(() => this.deletePaymentMethodLocally(ctx, paymentMethodWithIndex.index)),
      switchMap(() => ctx.dispatch(new PaymentMethodDeleted(paymentMethodWithIndex.paymentMethodInfo))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_DELETE_PAYMENT_METHOD_DEFAULT),
        NOT_FOUND: new RefreshWindow(MSG_DELETE_PAYMENT_METHOD_NOT_FOUND),
        TRANSACTION_WITH_SOLUTION_ID_IN_USE: new ShowErrorNotification(MSG_DELETE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE),
        OUTDATED_PAYMENT_INFORMATION_VERSION: new RefreshWindow(MSG_DELETE_PAYMENT_METHOD_OUTDATED_PAYMENT_INFORMATION_VERSION)
      })
    );
  }

  private deletePaymentMethodLocally(ctx: StateContext<MarketplacePaymentInformationStateModel>, index: number): Observable<unknown> {
    const methods = ctx.getState().paymentMethods;
    return ctx.dispatch(new SetPaymentMethods([...methods.slice(0, index), ...methods.slice(index + 1)]));
  }

  private getPaymentMethodWithIndex(
    ctx: StateContext<MarketplacePaymentInformationStateModel>,
    paymentMethodIdentifier: PaymentMethodIdentifier
  ): PaymentMethodInfoWithIndex {
    const methods = ctx.getState().paymentMethods;
    const index = methods.findIndex(paymentMethod =>
      isReusablePaymentMethodIdentifier(paymentMethodIdentifier)
        ? isReusablePaymentMethod(paymentMethod) && paymentMethod.id === paymentMethodIdentifier.id
        : paymentMethod.type === paymentMethodIdentifier.type
    );

    if (index > -1) {
      return { paymentMethodInfo: methods[index], index };
    } else {
      throw new InvalidStateError(`Payment method ${paymentMethodIdentifier} does not exist`);
    }
  }
}
